"use client";

/**
 * ApplyCta — Wrapper-Komponente für alle "Bewerbung starten"-CTAs auf
 * der Landing, die den Signature-Moment (Coin-Flip -> Crew-Pass -> /bewerben)
 * ausloesen sollen.
 *
 * Zwei Varianten via `variant`-Prop:
 *   - "magnetic" (default): MagneticCta-Look (Magnet-Hover) — für den
 *     dominanten Hero-CTA und den Footer-Echo-CTA.
 *   - "plain": einfacher next/link — für Sekundaer-CTAs in Reise-Cards
 *     etc., wo das Magnet-Verhalten zu aufdringlich waere.
 *
 * Beide Varianten reichen onClick = useApplyTransition()() durch, sodass:
 *   - Linkklick startet das Overlay statt direkt zu navigieren.
 *   - Modifier-Keys (Ctrl/Cmd/Shift) lassen die Default-Navigation zu
 *     ("Open in new tab" funktioniert weiterhin).
 *   - href bleibt /bewerben → korrekt für SEO/Crawler/Right-Click.
 */

import Link from "next/link";
import { useEffect, useState } from "react";
import { MagneticCta } from "./MagneticCta";
import { useApplyTransition } from "./useApplyTransition";

/**
 * Haengt die aktuelle URL-Query (utm_*, prefill, lock, job, …) an das
 * Ziel, wenn href auf den Funnel zeigt. Damit tragen "Startseite ->
 * Bewerben"-CTAs die selbe Attribution wie ein Direktlink /bewerben?utm=…
 * (Gruender 2026-08-03).
 */
function appendCurrentSearch(href: string, search: string): string {
  if (!search || search.length < 2) return href;
  // Nur an Funnel-Pfade anhaengen — keine externen Links verbiegen.
  if (!/^\/bewerben(?:$|[/?#])/.test(href)) return href;
  return href.includes("?") ? `${href}&${search.slice(1)}` : `${href}${search}`;
}

type CommonProps = {
  href: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  "aria-label"?: string;
  /** Tenant-Slug fuer das apply_click-Beacon (Karriere-Analytics Sub-Phase 2). */
  tenantSlug?: string;
};

export function ApplyCta({
  href,
  children,
  className,
  style,
  variant = "magnetic",
  tenantSlug,
  ...rest
}: CommonProps & { variant?: "magnetic" | "plain" }) {
  const startApply = useApplyTransition(tenantSlug ? { tenantSlug } : {});

  // Hydration-sicher: SSR + erstes Client-Render nutzen den plain href,
  // danach ergaenzen wir die aktuelle Query. Das Overlay/router.push macht
  // die eigentliche Navigation ohnehin selbst — dieser href zaehlt nur fuer
  // Right-Click / Middle-Click / SEO-Crawler.
  const [effectiveHref, setEffectiveHref] = useState(href);
  useEffect(() => {
    setEffectiveHref(appendCurrentSearch(href, window.location.search));
  }, [href]);

  // exactOptionalPropertyTypes: undefined-Props nur dann durchreichen, wenn
  // tatsaechlich gesetzt — sonst kollidiert es mit den strikten Ziel-Typen
  // von Link / MagneticCta, deren optionale Props "kein undefined" akzeptieren.
  const passthrough = {
    ...(className !== undefined ? { className } : {}),
    ...(style !== undefined ? { style } : {}),
    ...rest,
  };

  if (variant === "plain") {
    return (
      <Link href={effectiveHref} onClick={startApply} {...passthrough}>
        {children}
      </Link>
    );
  }

  return (
    <MagneticCta href={effectiveHref} onClick={startApply} {...passthrough}>
      {children}
    </MagneticCta>
  );
}
